import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {parse} from 'query-string';
import DebounceInput from 'react-debounce-input';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';

import {Keys} from '@stubhub/javascript-event-codes';
import {connect} from '@stubhub/react-store-provider';

import SearchSuggestionList from './components/SearchSuggestionList/SearchSuggestionListLazyload';
import {CMA_DEFAULTS, SEARCH_EXPANDED_VARIANT, SEARCH_MODAL_VARIANT} from './constants';
import controller from './controller';
import {messages} from './messages';

import '../styles/SearchBar.scss';

class SearchBar extends Component {
  getPlaceholderText(props) {
    const {placeholder} = messages;

    return props.intl.formatMessage(placeholder);
  }

  constructor(props, context) {
    super(props, context);
    this.state = {
      ...this.initState(props),
    };
    this.placeholderText = this.getPlaceholderText(props);
    this.searchInputRef = React.createRef();
    this.redirectModalRef = React.createRef();
  }

  /**
   * Method to initialize the state of the Search Component.
   * @param props [object] The initial props injected into the component.
   *
   */
  initState(props) {
    return {
      // Current value of the search input field
      searchTerm: this.getSearchTermFromURLParams(this.props),

      // Whether modal search is active. Can be true regardless of props.searchStyle.
      isModalSearchActive: (props && props.isModalSearchActive) || false,

      // Whether the search input field has focus
      isSearchActive: false,
    };
  }

  /**
   * Reset Search Component to Default State when the Search Style is SEARCH_MODAL_VARIANT
   *
   */
  closeSearch = () => {
    const {isModalSearchActive} = this.state;
    const {reset} = this.props;
    if (isModalSearchActive) {
      reset();
      this.clearSearch();
      this.updateIsModalSearchActive(false);
    } else {
      /**
       * For Non Modal Search Type Only reset the Suggestions.
       *
       */
      this.resetSearchSuggestion();
    }
    this.setState({
      isSearchActive: false,
    });
  };

  /**
   * Reset Search Component to Local and Component State by calling the reset reducer.
   *
   */
  clearSearch() {
    const {resetSearchComponent} = this.props;

    /**
     * Update local Component State to clear the Search Term
     *
     */
    this.setState({
      searchTerm: '',
    });

    /**
     * On Clear Event reset `searchSuggestions` and `term` from component controller.
     *
     */
    const searchSuggestions = [];
    const term = '';
    resetSearchComponent && resetSearchComponent({searchSuggestions, term});
  }

  /**
   * Reset the fetched search suggestions in the search  Search Component
   *
   */
  resetSearchSuggestion = () => {
    const {resetSearchComponent} = this.props;
    const searchSuggestions = [];
    resetSearchComponent && resetSearchComponent({searchSuggestions});
  };

  /**
   * Method to Validate the Selected Search Suggestion Index and correct the index if its out of bounds.
   * @param updatedSuggestionIndex [number] The current index pointing to the selected search suggestion.
   *
   */
  validateSuggestionIndex = (updatedSuggestionIndex) => {
    const {searchSuggestions} = this.props;
    const searchSuggestionsLength = searchSuggestions && searchSuggestions.length;

    if (searchSuggestionsLength) {
      if (updatedSuggestionIndex >= searchSuggestionsLength) {
        updatedSuggestionIndex = 0;
      } else if (updatedSuggestionIndex < 0) {
        updatedSuggestionIndex = searchSuggestionsLength - 1;
      }
    }

    return updatedSuggestionIndex;
  };

  /**
   * Method Get the Search Term from the URL Params.
   * @param props [object] The props passed to the object. Note 'location' prop is injected to the component by `withRouter`
   *
   */
  getSearchTermFromURLParams(props) {
    let searchTerm = (props && props.location && props.location.search) || '';
    if (searchTerm !== '' && searchTerm[0] === '?') {
      searchTerm = parse(searchTerm.substring(1)).q || '';
    }

    return searchTerm;
  }

  /**
   * Method which returns the next selectable item in the suggestions list based on the direction.
   * @param direction [string] This is the direction in the function is supposed to look for the next selectable event.
   * @returns selectedSuggestionIndex [number] The next selectable Item index.
   *
   */
  getNextSelectableEvent = (direction = 'up') => {
    let notSelectableItemFound = true;
    const {searchSuggestions} = this.props;
    let {selectedSuggestionIndex} = this.props;
    while (notSelectableItemFound) {
      if (direction === 'up') {
        selectedSuggestionIndex--;
      } else {
        selectedSuggestionIndex++;
      }
      selectedSuggestionIndex = this.validateSuggestionIndex(selectedSuggestionIndex);
      const searchSuggestion = searchSuggestions[selectedSuggestionIndex];
      if (!searchSuggestion.notSelectable) {
        notSelectableItemFound = false;
        break;
      }
    }

    return selectedSuggestionIndex;
  };

  /**
   * Method to construct the search suggestion query object and invoke the Search Suggestion API
   * @param searchTerm [string] Term for which the suggestions have to be fetched.
   * @param clearSearchSuggestions [boolean] Whether to clear the current search suggestions. Defaults to false.
   *
   */
  getSearchSuggestions = (searchTerm, clearSearchSuggestions = false) => {
    const {hideCbtEvents, point, suggestVariant, getSearchSuggestions} = this.props;
    let {searchSuggestions} = this.props;
    const {logger} = this.context;
    if (clearSearchSuggestions) {
      searchSuggestions = [];
    }

    const getSearchSuggestionsOptions = {
      term: searchTerm,
      point: hideCbtEvents ? undefined : point,
      logger,
      suggestVariant,
      searchSuggestions,
      geoExpansion: !hideCbtEvents,
      geoId: hideCbtEvents ? CMA_DEFAULTS.GEO_ID : undefined,
    };

    /**
     * Trigger the Search Suggestion API Call with New Parameters
     *
     */
    getSearchSuggestions(getSearchSuggestionsOptions);
  };

  /**
   * Method to determine the current searchStyle used by the component. This return variant is based on the `searchStyle` and `screenSize` and `isModalSearchActive` props and variables.
   * @returns currentSearchStyle [string] The string denoting the searchStyle i.e. either `SEARCH_MODAL_VARIANT` or `SEARCH_EXPANDED_VARIANT`.
   */
  getCurrentSearchStyle() {
    const {screenSize, searchStyle} = this.props;
    const {isModalSearchActive} = this.state;

    return searchStyle === SEARCH_MODAL_VARIANT || (screenSize === 'small' && isModalSearchActive)
      ? SEARCH_MODAL_VARIANT
      : SEARCH_EXPANDED_VARIANT;
  }

  /**
   * Method to Update the component state to enable Modal experience for Search. This also calls the `onModalSearchActiveChanged` callback
   * @param isModalSearchActive [boolean] The flag denoting if the Modal Search experience is active or inactive.
   *
   */
  updateIsModalSearchActive = (isModalSearchActive) => {
    const {isModalSearchActive: prevIsModalSearchActive} = this.state;
    if (prevIsModalSearchActive !== isModalSearchActive) {
      this.setState({
        isModalSearchActive,
      });

      /**
       * Call the `onModalSearchActiveChanged` callback to inform parent component of updates
       *
       */
      const {onModalSearchActiveChanged} = this.props;
      if (onModalSearchActiveChanged) {
        onModalSearchActiveChanged(isModalSearchActive);
      }
    }
  };

  /**
   * Wrapper Method to Handle the `up` or 'down' key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   * @param direction [string] The String representing the direction
   *
   */
  arrowKeyHandler = (event, direction) => {
    if (event) {
      event.preventDefault();
    }
    const {updateSelectedSuggestionIndex, searchSuggestions} = this.props;
    if (searchSuggestions && searchSuggestions.length) {
      const selectedSuggestionIndex = this.getNextSelectableEvent(direction);

      updateSelectedSuggestionIndex(selectedSuggestionIndex);
    }
  };

  /**
   * Wrapper Method to Handle the `esc` key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  escKeyHandler = (event) => {
    if (event) {
      event.preventDefault();
      event.target.blur();
    }
    this.clearSearch();
    this.closeSearch();
  };

  /**
   * Wrapper Method to Handle the `enter` key event.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  enterKeyHandler = (event) => {
    if (event) {
      event.preventDefault();
      event.target.blur();
    }

    const {searchSuggestions, selectedSuggestionIndex} = this.props;
    let searchSuggestion;

    /**
     * If `selectedSuggestionIndex` is set using the keyboard use that index to trigger the `searchItemClick`.
     *
     */
    if (selectedSuggestionIndex >= 0) {
      searchSuggestion = searchSuggestions[selectedSuggestionIndex];
    } else if (searchSuggestions) {
      const searchTerm = event.target.value;
      if (searchTerm) {
        /**
         * Else trigger the 'onSearchItemClick' using the last item in the `searchSuggestions` i.e. footer object.
         *
         */
        const searchSuggestionLastItemIndex = searchSuggestions.length - 1;
        searchSuggestion = searchSuggestions[searchSuggestionLastItemIndex] || {};

        /**
         * Use `event.target.value` instead of `this.state.searchTerm` as DebounceInput will trigger onChange with a latency.
         *
         */
        searchSuggestion.searchTerm = searchTerm;
      }
    }
    event.target.focus();
    searchSuggestion && this.onSearchItemClick({searchSuggestion})(event);
  };

  /**
   * Handler Method for the 'onKeyDownHandler' event listener.
   * @param event [object] The Synthetic React event object generated for the 'onKeydown' event listener.
   *
   */
  onKeyDownHandler = (event) => {
    const {keyCode} = event || {};
    const {esc, down, enter, up} = Keys;

    switch (keyCode) {
      case up:
        this.arrowKeyHandler(event, 'up');
        break;

      case down:
        this.arrowKeyHandler(event, 'down');
        break;

      case esc:
        this.escKeyHandler(event);
        break;

      case enter: {
        this.enterKeyHandler(event);
        break;
      }
    }
  };

  /**
   * Handler Method for the 'onInputFocusHandler' event listener.
   */
  onInputFocusHandler = () => {
    const {searchTerm, isSearchActive} = this.state;
    const {notifySell} = this.props;

    /**
     * If Search is not Active
     *
     */
    if (!isSearchActive) {
      /**
       * On Input Focus Preload the Suggestion List Component.
       *
       */
      SearchSuggestionList.preload();
      notifySell && notifySell({isSearchActive: true});
      this.setState({
        isSearchActive: true,
      });
    }

    /**
     * Activate the Modal Search if current searchType `SEARCH_MODAL_VARIANT` or `screenSize` is small
     *
     */
    const {searchStyle, screenSize} = this.props;
    this.updateIsModalSearchActive(searchStyle === SEARCH_MODAL_VARIANT || screenSize === 'small');

    /**
     * Display Search Suggestions automatically `onfocus` when Search Component's input box is in focus.
     *
     */
    if (searchTerm) {
      // Search term present. Load search suggestions from backend.
      this.getSearchSuggestions(searchTerm);
    }
  };

  onInputFocusOutHandler = () => {
    const {notifySell} = this.props;
    notifySell && notifySell({isSearchActive: false});
  };

  /**
   * Handler Method for the 'onChange' event listener.
   * @param event [object] The Synthetic React event object generated for the 'onChange' event listener.
   *
   */
  handleChange = (event) => {
    const {searchTerm: oldSearchTerm} = this.state;
    const searchTerm = event.target.value;

    if (!searchTerm) {
      this.clearSearch();
    } else {
      const oldValue = oldSearchTerm;

      /**
       * Set the Updated Search Term into the Component State
       *
       */
      this.setState({searchTerm});

      /*
       * Fetch the search suggestions. Clear the search suggestions if switching from empty text box (potentially
       * showing RV) to non-empty text box (search suggestions).
       */
      this.getSearchSuggestions(searchTerm, oldValue === '');
    }
  };

  /**
   * Wrapper callback to clear te Search Component State.
   * @param event [object] The Synthetic Click Event Associated to onCLick
   *
   */
  onClearClick = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.clearSearch();
  };

  /**
   * Wrapper callback to Close te Search Component Modal.
   * @param event [object] The Synthetic Click Event Associated to onCLick
   *
   */
  onCloseClick = (event) => {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.closeSearch();
  };

  /**
   * The Method invoked when a search item within the list of search results OR when the search button is clicked.
   * @param opts [object] the param containing `searchSuggestion` object for triggering the `onSearchItemClick` method.
   */
  onSearchItemClick = (opts) => {
    const {onSearchItemClick, app, history} = this.props;

    return (event) => {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.target.blur();
      }

      if (opts) {
        this.closeSearch();
        if (typeof onSearchItemClick === 'function') {
          onSearchItemClick(opts, history, app); // History is provided by withRouter HOC
        }
      }
    };
  };

  /**
   * Handler Method for the 'mousedown' event listener.
   * @param event [object] The event object generated for the 'mousedown' event listener.
   *
   */
  handleOutsideClicks = (event) => {
    const {searchInputRef} = this;
    const {screenSize, searchStyle, notifySell} = this.props;
    const {isSearchActive} = this.state;

    notifySell && notifySell({isSearchActive});

    /**
     * Click handler Activation criteria for `SEARCH_EXPANDED_VARIANT`.
     *
     */
    const searchExpandedEntryCondition =
      isSearchActive && searchStyle === SEARCH_EXPANDED_VARIANT && screenSize !== 'small';

    /**
     * Click handler Activation criteria for `SEARCH_MODAL_VARIANT`.
     *
     */
    const searchModalEntryCondition =
      isSearchActive && searchStyle === SEARCH_MODAL_VARIANT && screenSize !== 'small' && screenSize !== 'medium';

    if (
      (searchExpandedEntryCondition || searchModalEntryCondition) &&
      searchInputRef &&
      !searchInputRef.contains(event.target) &&
      !this.redirectModalRef?.current
    ) {
      this.closeSearch();
    }
  };

  /**
   * React Lifecycle Methods
   *
   */
  UNSAFE_componentWillReceiveProps(nextProps) {
    /**
     * Update 'isModalSearchActive' state variable if not already set for searchStyle `SEARCH_MODAL_VARIANT`
     *
     */
    const {searchStyle, screenSize: prevScreenSize} = this.props;

    /**
     * Update the Local state `isModalSearchActive` when props have changes for `SEARCH_MODAL_VARIANT`.
     *
     */
    if (searchStyle === SEARCH_MODAL_VARIANT) {
      const {isModalSearchActive} = nextProps;
      this.updateIsModalSearchActive(isModalSearchActive);
    }

    /**
     * Update the Local state `isModalSearchActive` and global redux state when screenSize when props have changes for `SEARCH_EXPANDED_VARIANT`.
     *
     */
    const {isModalSearchActive, isSearchActive} = this.state;
    const {screenSize} = nextProps;
    if (searchStyle === SEARCH_EXPANDED_VARIANT && isSearchActive && prevScreenSize !== screenSize) {
      if (isModalSearchActive && screenSize !== 'small') {
        this.updateIsModalSearchActive(false);
      } else if (!isModalSearchActive && screenSize === 'small' && isSearchActive) {
        this.updateIsModalSearchActive(true);
      }
    }

    /**
     * Update Search Term in the Component if the Term has changed in the URL.
     *
     */
    const searchTermFromNextProps = this.getSearchTermFromURLParams(nextProps);
    if (searchTermFromNextProps !== this.getSearchTermFromURLParams(this.props)) {
      this.setState({
        searchTerm: searchTermFromNextProps,
      });
    }
  }

  componentDidMount() {
    /**
     * Attach an event listener on `document` to give callback for `mousedown` events.
     *
     */
    typeof document !== 'undefined' && document.addEventListener('mousedown', this.handleOutsideClicks);
  }

  componentWillUnmount() {
    /**
     * Remove any event listener on `document` for `mousedown` events.
     *
     */
    typeof document !== 'undefined' && document.removeEventListener('mousedown', this.handleOutsideClicks);
  }

  componentDidUpdate({isSearchAutoFocus: prevSearchAutoFocus}) {
    const {isSearchAutoFocus} = this.props;
    if (prevSearchAutoFocus !== isSearchAutoFocus && isSearchAutoFocus && this.searchInputRef?.current) {
      this.searchInputRef.current.focus();
    }
  }

  /**
   * Returns the properly configured SearchSuggestionList component.
   */
  getSearchSuggestionListComp() {
    const {isSearchActive} = this.state;
    const {
      term,
      searchSuggestions = [],
      selectedSuggestionIndex,
      updateSelectedSuggestionIndex,
      deviceType,
      merchClassName,
      query,
      isRedirectModalEnabled,
      app,
    } = this.props;
    const {term: searchTerm} = query || {};
    let {response} = this.props;

    let showSearchSuggestion = false;
    let searchBarResultsView = null;

    if (isSearchActive) {
      // Search bar has focus
      if (term && searchSuggestions.length) {
        // A search term is typed in. Show search suggestions.
        showSearchSuggestion = true;
      }
      if (query && searchTerm !== term) {
        response = '';
      }

      if (showSearchSuggestion) {
        searchBarResultsView = (
          <SearchSuggestionList
            onSearchItemClick={this.onSearchItemClick}
            term={term}
            searchSuggestions={searchSuggestions}
            selectedSuggestionIndex={selectedSuggestionIndex}
            updateSelectedSuggestionIndex={updateSelectedSuggestionIndex}
            deviceType={deviceType}
            query={query}
            response={response}
            className={merchClassName}
            isRedirectModalEnabled={isRedirectModalEnabled}
            app={app}
            modalRef={this.redirectModalRef}
          />
        );
      }
    }

    return searchBarResultsView;
  }

  render() {
    let searchBarResultsView;
    const {searchTerm = ''} = this.state;
    const {loading, searchStyle, isModalSearchActive, merchClassName, isSearchAutoFocus, screenSize, intl} = this.props;
    let searchComponent;

    /**
     * If `searchStyle` is `SEARCH_MODAL_VARIANT` and the component is not active then render an empty component
     *
     */
    if (searchStyle === SEARCH_MODAL_VARIANT && !isModalSearchActive) {
      searchComponent = null;
    } else {
      searchBarResultsView = this.getSearchSuggestionListComp(this.props);

      /**
       * Determine to Show Modal or Expanded Experience
       *
       */
      const isModal = this.getCurrentSearchStyle() === SEARCH_MODAL_VARIANT;

      let searchBarContainerCls = 'SearchBar__container';
      let searchBarCls = 'SearchBar';
      let searchBarViewCls = 'SearchBar__view';

      searchBarContainerCls += isModal ? ' SearchBar__container--modal' : ' SearchBar__container--expanded';
      searchBarCls += isModal ? ' SearchBar--modal' : ' SearchBar--expanded';
      searchBarViewCls += isModal ? ' SearchBar__view--modal' : ' SearchBar__view--expanded';

      if (merchClassName) {
        searchBarCls += ` ${merchClassName}`;
      }

      /**
       * Autofocus on the Input box for Search Modal experience
       *
       */
      const autoFocus = isModal ? true : null;

      searchComponent = (
        <div className={searchBarContainerCls}>
          <form onSubmit={this.onSearchItemClick({searchSuggestion: {searchTerm}})} action="/find/s">
            <div
              className={searchBarCls}
              ref={(node) => {
                this.searchInputRef = node;
              }}
            >
              <div className={searchBarViewCls}>
                {merchClassName ? <span alt="Search" className="SearchBar__icon--search" /> : null}
                <DebounceInput
                  className="edit"
                  type="text"
                  maxLength="2048"
                  name="q"
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  title={intl.formatMessage(messages.searchInputTitle)}
                  placeholder={this.placeholderText}
                  debounceTimeout={1000}
                  value={searchTerm}
                  onChange={this.handleChange}
                  autoFocus={screenSize === 'small' ? autoFocus : isSearchAutoFocus || null}
                  onKeyDown={this.onKeyDownHandler}
                  onFocus={this.onInputFocusHandler}
                  onBlur={this.onInputFocusOutHandler}
                  inputRef={this.searchInputRef}
                />
                {this.getCurrentSearchStyle() === SEARCH_EXPANDED_VARIANT && loading ? (
                  <span className="SearchBar__button SearchBar__button--loading animated infinite spin" />
                ) : null}
                {/* Note that the "clear" button can still be hidden based on screen size, when in modal operation */}
                {searchTerm.length ? (
                  <span
                    alt="Clear"
                    className="SearchBar__button SearchBar__button--clear"
                    onClick={this.onClearClick}
                  />
                ) : null}
                {isModal && (
                  <button
                    type="button"
                    className="SearchBar__button SearchBar__button--close"
                    onClick={this.onCloseClick}
                  />
                )}
              </div>
              {searchBarResultsView}
            </div>
          </form>
        </div>
      );
    }

    return searchComponent;
  }
}

SearchBar.propTypes = {
  /**
   * Internationalization object provided by react-intl.
   */
  intl: PropTypes.object.isRequired,

  /**
   * HTML5 History API compliant object used for navigation.
   */
  history: PropTypes.object,

  /**
   * The configured search style. Can be either SEARCH_EXPANDED_VARIANT or SEARCH_MODAL_VARIANT.
   * Note: It may be different from the current searchStyle obtained using `getCurrentSearchStyle`.
   */
  searchStyle: PropTypes.string,

  /**
   * Whether modal search is active (true). Relevant for any value of searchStyle.
   */
  isModalSearchActive: PropTypes.bool,

  /**
   * Callback called when the Search Modal is shown.
   */
  onModalSearchActiveChanged: PropTypes.func,

  /**
   * Array of search suggestions or recently viewed event suggestions.
   */
  searchSuggestions: PropTypes.array,

  /**
   * Function to update the selected suggestion index.
   */
  updateSelectedSuggestionIndex: PropTypes.func.isRequired,

  /**
   * Function to reset the search component.
   */
  resetSearchComponent: PropTypes.func.isRequired,

  /**
   * Function to reset the component.
   */
  reset: PropTypes.func,

  /**
   * Callback function when a search item is clicked.
   */
  onSearchItemClick: PropTypes.func.isRequired,

  /**
   * String identifying a point in the application.
   */
  point: PropTypes.string,

  /**
   * Screen size identifier, typically 'small', 'medium', or 'large'.
   */
  screenSize: PropTypes.string,

  /**
   * Boolean flag indicating if search autofocus is enabled.
   */
  isSearchAutoFocus: PropTypes.bool,

  /**
   * String className specific to Merch Header related stylings.
   */
  merchClassName: PropTypes.string,

  /**
   * Variant number for suggesting.
   */
  suggestVariant: PropTypes.number,

  /**
   * Index of the currently selected suggestion in the search suggestions array.
   */
  selectedSuggestionIndex: PropTypes.number,

  /**
   * Boolean flag to determine whether to hide CBT events.
   */
  hideCbtEvents: PropTypes.bool,

  /**
   * Function to retrieve search suggestions.
   */
  getSearchSuggestions: PropTypes.func,

  /**
   * Function to notify the search activation state.
   */
  notifySell: PropTypes.func,

  /**
   * Boolean flag to determine whether search bar is loading.
   */
  loading: PropTypes.bool,

  /**
   * The application name or identifier.
   */
  app: PropTypes.string,

  /**
   * The search term to be used in search queries.
   */
  term: PropTypes.string,

  /**
   * The type of device from which the search is triggered.
   */
  deviceType: PropTypes.string,

  /**
   * The query parameters related to the search.
   */
  query: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  /**
   * Flag indicating whether the redirect modal is enabled for search.
   */
  isRedirectModalEnabled: PropTypes.bool,

  /**
   * Response data, which can be either a string or an object.
   */
  response: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

// Default props for SearchBar component
SearchBar.defaultProps = {
  searchStyle: SEARCH_EXPANDED_VARIANT,
  isModalSearchActive: false,
  searchSuggestions: [],
  screenSize: 'small',
  suggestVariant: 0,
};

SearchBar.contextTypes = {
  /**
   * Logger object used for logging within the component.
   */
  logger: PropTypes.object,

  /**
   * Global registry object for accessing application-wide state or services.
   */
  globalRegistry: PropTypes.object,

  /**
   * Router object for handling navigation within the application.
   */
  router: PropTypes.object,
};

/**
 * This export is for testing (no Redux store)
 * withRouter - Injects 'location' object into the component's props.
 *
 */
export const SearchBarTest = injectIntl(SearchBar);

/**
 * This export is for real component
 * withRouter - Injects 'location' object into the component's props.
 *
 */
export default withRouter(connect(controller)(SearchBarTest));
